import React, { useState, useEffect, useMemo } from "react";
import styles from "./GalleryHome.module.css";
import SeriesCard from "../../components/Furniture-Gallery/SeriesCard";
import { getAllSeries } from "../../service/galleryService";
import LoadingSpinner from "../../components/LoadingSpinner";
import GalleryHero from "../../components/GalleryHero";

const GalleryHome = () => {
  const [series, setSeries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchSeries = async () => {
      try {
        const data = await getAllSeries();
        if (isMounted) {
          setSeries(data);
          setLoading(false);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message || "Failed to load furniture series");
          setLoading(false);
        }
      }
    };

    fetchSeries();

    return () => {
      isMounted = false;
    };
  }, []);

  // Memoize series cards to prevent unnecessary re-renders
  const seriesCards = useMemo(() => {
    return series.map((s, index) => (
      <SeriesCard
        key={s._id}
        series={s}
        delay={index * 100} // Stagger animation
      />
    ));
  }, [series]);

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingSpinner />
        <p className={styles.loadingText}>Loading furniture collections...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorIcon}>⚠️</div>
        <div className={styles.errorMessage}>{error}</div>
        <button
          className={styles.retryButton}
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={styles.galleryContainer}>
      <GalleryHero />
      <div className={styles.galleryContent}>
        <div className={styles.headerSection}>
          <div className={styles.titleWrapper}>
            <div className={styles.titleDecoration}></div>
          </div>
          <p className={styles.sectionDescription}>
            Explore our comprehensive range of commercial furniture designed for
            durability, comfort, and style
          </p>
          <div className={styles.statsContainer}>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>{series.length}</span>
              <span className={styles.statLabel}>Series</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statNumber}>
                {series.reduce((acc, s) => acc + (s.models?.length || 0), 0)}
              </span>
              <span className={styles.statLabel}>Models</span>
            </div>
          </div>
        </div>

        <div className={styles.gridContainer}>
          {series.length > 0 ? (
            seriesCards
          ) : (
            <div className={styles.noResults}>
              <div className={styles.noResultsIcon}>📦</div>
              <h3>No furniture series found</h3>
              <p>Please check back later for new collections</p>
            </div>
          )}
        </div>
      </div>

      <div className={styles.ctaSection}>
        <div className={styles.ctaBackground}></div>
        <div className={styles.ctaContent}>
          <h3 className={styles.ctaTitle}>Bulk Order Inquiries?</h3>
          <p className={styles.ctaText}>
            Contact us for special pricing on bulk orders and customized
            solutions tailored to your needs
          </p>
          <button
            className={styles.ctaButton}
            onClick={() => (window.location.href = "/contact")}
          >
            <span>Request Quote</span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M5 12H19M19 12L12 5M19 12L12 19"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(GalleryHome);
